import { useDispatch } from "react-redux";
import Xarrow from "react-xarrows";
import { importFromYaml } from "../../../../abstract/controller/modelYamlImport";
import * as tutorialUtils from "../../../../abstract/controller/utils/tutorialUtils";
import { useArrowSync } from "../../../../abstract/hook/useArrowSync";
import { setRoot } from "../../../../abstract/redux/slice/structureSlice";
import ButtonIntroWide from "../../../ui/buttons/ButtonIntroWide";
import ButtonTomatoClose from "../../../ui/buttons/ButtonTomatoClose";
import styles from "./Manual.module.scss";

const id = "struct7";
const idNext = "struct8";
const idPrev = "struct6";

const next = `
global parameters:
  - parameter: character
    parameters:
      - parameter: weapon
        choices:
          - choice: dagger
          - choice: ranged
            choices:
              - choice: bow
              - choice: crossbow
          - choice: melee
            choices:
              - choice: axe
              - choice: mace
              - choice: sword
      - parameter: name
        choices:
          - choice: hero
            choices:
              - choice: Peter
              - choice: Susan
              - choice: Edmund
              - choice: Lucy
          - choice: foe
            choices:
              - choice: witch
              - choice: cyclops
              - choice: giant
    logic:
      - constraint: Girls use daggers
        expression: "'name' IN ['hero::Susan', 'hero::Lucy'] => 'weapon' IS 'dagger'"
functions:
  - function: simple
    parameters:
      - linked parameter: hero
        linked to: character
      - linked parameter: foe
        linked to: character
      - parameter: location
        choices:
          - choice: forest
          - choice: castle
          - choice: mountain
          - choice: river
          - choice: lake
    logic:
      - constraint: Heroes have friendly names
        expression: "'hero::name' IS 'hero'"
      - constraint: Foes are hostile creatures
        expression: "'foe::name' IS 'foe'"
`;

const prev = `
global parameters:
  - parameter: character
    parameters:
      - parameter: weapon
        choices:
          - choice: dagger
          - choice: ranged
            choices:
              - choice: bow
              - choice: crossbow
          - choice: melee
            choices:
              - choice: axe
              - choice: mace
              - choice: sword
      - parameter: name
        choices:
          - choice: hero
            choices:
              - choice: Peter
              - choice: Susan
              - choice: Edmund
              - choice: Lucy
          - choice: foe
            choices:
              - choice: witch
              - choice: cyclops
              - choice: giant
    logic:
      - constraint: Girls use daggers
        expression: IF 'character::name' IN ['Susan', 'Lucy'] THEN 'character::weapon' IS 'dagger'
functions:
  - function: simple
    parameters:
      - parameter: location
        choices:
          - choice: forest
          - choice: castle
          - choice: mountain
          - choice: river
          - choice: lake
`;

function ManualStructures7({ tutorial, setTutorial }) {
  if (tutorial === id) {
    return <Core setTutorial={setTutorial} />;
  }

  return null;
}

function Core({ setTutorial }) {
  const _dispatch = useDispatch();
  const renderTrigger = useArrowSync();

  const handleShowMe = () => {
    const { model } = importFromYaml(next);
    _dispatch(setRoot(model, 0));
  };

  const handleBack = () => {
    const { model } = importFromYaml(prev);
    _dispatch(setRoot(model, 0));
    setTutorial(idPrev);
  };

  const handleClose = () => {
    setTutorial("null");
  };

  return (
    <>
      {document.getElementById("header_logic") && !document.getElementById("logic_constraint") && (
        <div className={`${styles["container"]} ${styles["container-st7"]} ${styles["overlay"]}`}>
          <div id="tutorial-text-st7" className={`${styles["container__text"]}`}>
            <p>To show/hide logic click on the header.</p>
          </div>

          <Xarrow
            key={`arrow-1-${renderTrigger}`}
            start="tutorial-text-st7"
            end="header_logic"
            color="#415B32"
            strokeWidth={2}
            path="smooth"
            curveness={0.8}
            headSize={6}
          />
        </div>
      )}

      <div className={`${styles["foreground"]} ${styles["foreground-st7"]}  ${styles["overlay"]}`}>
        <ButtonTomatoClose onClick={handleClose} />
        <div className={styles["header"]}>
          <div className={styles["header__assistant"]}></div>
          <div className={styles["header__text--main"]}>
            <p>
              Don't forget that nested nodes can also be used in function logic! Now,
              let's make sure our heroes only get heroic names, and our foes are always vile
              creatures.
            </p>
            <p>
              Create two constraints. Name the first one <b>Heroes have friendly names</b>{" "}
              with the expression <b>'hero::name' IS 'hero'</b>. Name the second one{" "}
              <b>Foes are hostile creatures</b> with the expression <b>'foe::name' IS 'foe'</b>.
            </p>
          </div>
        </div>
        <div className={styles["confirmation"]}>
          <div className={styles["button"]}>
            <ButtonIntroWide theme="red" title="Go back!" onClick={handleBack} />
          </div>
          <div className={styles["button"]}>
            <ButtonIntroWide theme="tomato" title="Show me!" onClick={handleShowMe} />
          </div>
        </div>
      </div>
    </>
  );
}

export const manualValidateStructures7 = (root, tutorial, setTutorial, setMessage) => {
  if (tutorial === id) {
    let { res: resFun, msg: msgFun } = tutorialUtils.validateSingleFunction(root);

    if (!resFun) {
      setMessage(msgFun);
      return false;
    }

    let { res: resLogicA, msg: msgLogicA } = tutorialUtils.validateCondition(
      root,
      "root→[function]→simple→[logic]→Heroes have friendly names",
      "'hero::name' IS 'hero'",
    );

    if (!resLogicA) {
      setMessage(msgLogicA);
      return false;
    }

    let { res: resLogicB, msg: msgLogicB } = tutorialUtils.validateCondition(
      root,
      "root→[function]→simple→[logic]→Foes are hostile creatures",
      "'foe::name' IS 'foe'",
    );

    if (!resLogicB) {
      setMessage(msgLogicB);
      return false;
    }

    setTutorial(idNext);

    setMessage("");
    return true;
  }

  return false;
};

export default ManualStructures7;
